// useOnStartLayers.ts - Clean, focused hook for loading layers on startup

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLayers, useWmsToTileLayer } from '@ais-ui/react-openlayers';

import {
  useFavoriteLayerData,
  useLayerOrderingSettings,
  useShapeManagement,
} from './useLayerOrdering';
import {
  LayerLoadingDependencies,
  loadSingleLayerByType,
} from './layerLoadingUtils';
import { loadLegacyFavoriteLayers } from './legacyLayerLoader';
import { useToast } from '@ais-ui/ui-core';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useGetSourceIdentifiers, useShapeFetch } from '@ais-ui/api';
import { useSession } from '@ais-ui/websockets';
import { useCoverage } from '../Coverage/CoverageContext';
import { useFilterContext } from '../FilterContext';
import { useRemoveWmsLayer } from '../LayerControl/Actions/WMSLayers';
import { useSettings } from '../../Settings/SettingsProvider';
import { useSyncLayerOrdering } from './useSyncLayerOrdering';

export const useOnStartLayers = () => {
  const loadedLayersOnStart = useRef(false);
  const coverageLayerPending = useRef(false);

  // Core dependencies
  const { doesLayerExist, addLayer, isCanvasLoaded, addExistingLayerItem } =
    useLayers();
  useSyncLayerOrdering();
  const { settings } = useSettings();
  const { toast } = useToast();
  const { olSession } = useSession();
  const permissions = usePermissions();

  // Data hooks - now also getting isLoading state
  const { data, isLoading: isLoadingSourceIdentifiers } =
    useGetSourceIdentifiers(permissions.coverage);
  const sourceIdentifiers = useMemo(() => data || [], [data]);

  // Custom hooks
  const {
    saveLayerOrderingToSettings,
    createInitialLayerOrdering,
    getExistingLayerOrdering,
  } = useLayerOrderingSettings();

  const { favoriteShapes, favoriteFilters, enabledWmsLayers } =
    useFavoriteLayerData();

  const { removeShapeFromFavorites } = useShapeManagement();

  // Layer loading dependencies
  const shapeFetch = useShapeFetch();
  const { addSourceIdentifiersToMap } = useCoverage();
  const filterContext = useFilterContext();
  const removeWmsLayer = useRemoveWmsLayer();
  const onWmsToTileLayerWithLayerItem = useWmsToTileLayer(removeWmsLayer);

  // Create dependencies object for layer loading
  const layerLoadingDeps: LayerLoadingDependencies = useMemo(
    () => ({
      settings, // Pass actual settings instead of empty object
      enabledWmsLayers,
      sourceIdentifiers,
      doesLayerExist,
      shapeFetch,
      addLayer,
      addSourceIdentifiersToMap,
      addExistingLayerItem,
      onWmsToTileLayerWithLayerItem,
      removeShapeFromFavorites,
      toast,
      olSession,
      filterContext,
    }),
    [
      settings,
      enabledWmsLayers,
      sourceIdentifiers,
      doesLayerExist,
      shapeFetch,
      addLayer,
      addSourceIdentifiersToMap,
      addExistingLayerItem,
      onWmsToTileLayerWithLayerItem,
      removeShapeFromFavorites,
      toast,
      olSession,
      filterContext,
    ]
  );

  // Load layers using saved ordering
  const loadLayersFromOrdering = useCallback(
    async (layerOrdering: any[]) => {
      // Track coverage layer config if it exists but data isn't ready
      let pendingCoverageLayer = null;

      // Process layers in sequence (not parallel) to maintain order
      for (let i = 0; i < layerOrdering.length; i++) {
        const layerConfig = layerOrdering[i];

        // Skip ships layer as it's handled elsewhere
        if (layerConfig.type === 'ships') continue;

        // Determine position: everything before ships index = below, everything after = above
        const shipsIndex = layerOrdering.findIndex((l) => l.type === 'ships');
        const position =
          shipsIndex !== -1 && i < shipsIndex ? 'below-ships' : 'top';

        // Special handling for coverage layer
        if (
          layerConfig.type === 'coverage' &&
          layerConfig.id === 'source-identifiers'
        ) {
          // If source identifiers are still loading, save config for later
          if (isLoadingSourceIdentifiers) {
            pendingCoverageLayer = { layerConfig, position };
            coverageLayerPending.current = true;
            continue; // Skip this layer for now
          }
          // If data is empty (loaded but no results), skip the layer
          if (!sourceIdentifiers || sourceIdentifiers.length === 0) {
            console.log(
              'Source identifiers loaded but empty, skipping coverage layer'
            );
            continue;
          }
        }

        // Load all other layers immediately
        await loadSingleLayerByType(layerConfig, position, layerLoadingDeps);

        // Small delay to ensure layer is fully processed before next one
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      return pendingCoverageLayer;
    },
    [layerLoadingDeps, isLoadingSourceIdentifiers, sourceIdentifiers]
  );

  // Main layer loading logic
  const loadLayersInOrder = useCallback(async () => {
    const existingLayerOrdering = getExistingLayerOrdering();

    if (
      settings?.['ribbonPlugin.settings']?.layer_ordering &&
      existingLayerOrdering
    ) {
      await loadLayersFromOrdering(existingLayerOrdering);
    } else {
      // Legacy fallback: load using old method and create initial ordering
      await loadLegacyFavoriteLayers({
        settings,
        sourceIdentifiers,
        favoriteShapes,
        favoriteFilters,
        enabledWmsLayers,
        addSourceIdentifiersToMap,
        addLayer,
        addExistingLayerItem,
        doesLayerExist,
        shapeFetch,
        onWmsToTileLayerWithLayerItem,
        removeShapeFromFavorites,
        olSession,
        filterContext,
        toast,
      });

      // Create and save initial ordering for future use
      const initialOrdering = createInitialLayerOrdering();
      saveLayerOrderingToSettings(initialOrdering);
    }
  }, [
    settings,
    getExistingLayerOrdering,
    loadLayersFromOrdering,
    sourceIdentifiers,
    favoriteShapes,
    favoriteFilters,
    enabledWmsLayers,
    addSourceIdentifiersToMap,
    addLayer,
    addExistingLayerItem,
    doesLayerExist,
    shapeFetch,
    onWmsToTileLayerWithLayerItem,
    removeShapeFromFavorites,
    olSession,
    filterContext,
    toast,
    createInitialLayerOrdering,
    saveLayerOrderingToSettings,
  ]);

  // Main effect to trigger layer loading
  useEffect(() => {
    if (loadedLayersOnStart.current || !isCanvasLoaded) {
      return;
    }

    // Load all layers immediately (coverage will be skipped if data not ready)
    loadLayersInOrder();
    loadedLayersOnStart.current = true;
  }, [isCanvasLoaded, loadLayersInOrder]);

  // Separate effect to handle delayed coverage layer loading
  useEffect(() => {
    // Only proceed if we were waiting for coverage and it's now loaded
    if (!coverageLayerPending.current || isLoadingSourceIdentifiers) {
      return;
    }

    // Check if we now have data
    if (!sourceIdentifiers || sourceIdentifiers.length === 0) {
      console.log('Source identifiers finished loading but no data available');
      coverageLayerPending.current = false;
      return;
    }

    // Check if layer already exists (might have been added manually)
    if (doesLayerExist('source-identifiers')) {
      coverageLayerPending.current = false;
      return;
    }

    // Get the layer ordering to find the correct position
    const existingLayerOrdering = getExistingLayerOrdering();
    if (!existingLayerOrdering) {
      coverageLayerPending.current = false;
      return;
    }

    // Find the coverage layer config
    const coverageConfig = existingLayerOrdering.find(
      (layer) => layer.type === 'coverage' && layer.id === 'source-identifiers'
    );

    if (!coverageConfig) {
      coverageLayerPending.current = false;
      return;
    }

    // Determine position
    const coverageIndex = existingLayerOrdering.indexOf(coverageConfig);
    const shipsIndex = existingLayerOrdering.findIndex(
      (l) => l.type === 'ships'
    );
    const position =
      shipsIndex !== -1 && coverageIndex < shipsIndex ? 'below-ships' : 'top';

    // Load the coverage layer now that data is ready
    loadSingleLayerByType(coverageConfig, position, layerLoadingDeps);
    coverageLayerPending.current = false;
  }, [
    isLoadingSourceIdentifiers,
    sourceIdentifiers,
    doesLayerExist,
    getExistingLayerOrdering,
    layerLoadingDeps,
  ]);
};
